/**
 * Road tool: drag-to-paint road tiles. Pointer down starts a stroke,
 * pointer move (while down) appends each newly-entered grid square,
 * pointer up commits the stroke as a single placeMany command so the
 * whole stroke undoes as one operation.
 *
 * Filtering:
 *   - Squares already containing a road are skipped so the user can paint
 *     freely without creating duplicate-on-same-tile warnings.
 *   - Squares occupied by a non-road building still get a road placed on
 *     top; the validator catches the overlap (Phase 2's permissive rule).
 *   - Squares outside the unlocked area still get a road placed; the
 *     validator catches the outside-area issue.
 */
import type { GridCoord, ImportedCity, Plan, ResolvedBuilding } from '$lib/types'
import { resolveBuilding } from '$lib/catalogue'
import type { ToolPointerHandlers } from '$lib/renderer'
import buildingInfo from '../../../data/buildingInfo.json'

/**
 * Pinned default road. "Path" — the cheapest, earliest-era road every
 * player has access to. The fallback chain in resolveDefaultRoad covers
 * the case where this id ever disappears from the catalogue (a future
 * game patch could rename it; the resolver will land on any `street`-
 * typed entity it can find instead).
 */
export const DEFAULT_ROAD_ENTITY_ID = 'S_BronzeAge_Street'

/**
 * Resolve the road entity to use for a paint stroke. Tries the pinned
 * default first, then any `isRoad` entity in the import's CityEntities,
 * then the reference catalogue. Returns null if nothing road-typed exists
 * (the editor disables the road button in that case).
 */
export function resolveDefaultRoad(
  importedCity: ImportedCity | null,
): ResolvedBuilding | null {
  const pinned = resolveBuilding(DEFAULT_ROAD_ENTITY_ID, importedCity ?? undefined)
  if (pinned?.isRoad) return pinned

  if (importedCity) {
    for (const id of Object.keys(importedCity.parsed.cityEntities)) {
      const r = resolveBuilding(id, importedCity)
      if (r?.isRoad) return r
    }
  }

  const slim = buildingInfo as { id: string; type?: string }[]
  for (const entry of slim) {
    if (entry.type !== 'street') continue
    const r = resolveBuilding(entry.id, importedCity ?? undefined)
    if (r?.isRoad) return r
  }

  return null
}

export interface RoadToolContext {
  /** Read the live plan. Used to filter out already-roaded tiles. */
  getPlan(): Plan | null
  /** Imported city for catalogue resolution. */
  getImportedCity(): ImportedCity | null
  /** Commit the stroke as a single placeMany command. */
  commitStroke(placements: ReadonlyArray<{ cityentityId: string; at: GridCoord }>): void
  /** Render translucent road squares as the user paints. */
  drawStrokePreview(squares: ReadonlyArray<GridCoord>): void
  /** Wipe the preview (stroke committed or cancelled). */
  clearStrokePreview(): void
}

export function makeRoadHandlers(ctx: RoadToolContext): ToolPointerHandlers {
  /** Squares the cursor has visited during the current stroke, in entry
   *  order. Duplicates allowed (the user can paint over their own line);
   *  they're folded out at commit time. */
  let stroke: GridCoord[] = []
  let isPainting = false

  function reset() {
    stroke = []
    isPainting = false
    ctx.clearStrokePreview()
  }

  return {
    onPointerDown(e) {
      if (e.button !== 0) return
      isPainting = true
      stroke = [e.grid]
      ctx.drawStrokePreview(stroke)
    },

    onPointerMove(e) {
      if (!isPainting) return
      const last = stroke[stroke.length - 1]
      if (last && last.x === e.grid.x && last.y === e.grid.y) return
      stroke.push(e.grid)
      ctx.drawStrokePreview(stroke)
    },

    onPointerUp(e) {
      if (e.button !== 0) return
      if (!isPainting) return

      const plan = ctx.getPlan()
      const importedCity = ctx.getImportedCity()
      const road = resolveDefaultRoad(importedCity)
      if (!plan || !road) {
        // Either no plan loaded or no road entity exists in the catalogue.
        // Drop the stroke without committing.
        reset()
        return
      }

      const placements = filterStroke(stroke, plan, importedCity, road.id)
      reset()
      if (placements.length > 0) {
        ctx.commitStroke(placements)
      }
    },

    onCancel() {
      if (!isPainting) return false
      reset()
      return true
    },

    onDeactivate() {
      if (isPainting) reset()
    },
  }
}

/**
 * Build the placeMany payload from a stroke: dedupe squares, drop ones
 * that already contain a road. Exposed so future tests can pin behaviour
 * without spinning up a Pixi scene.
 */
export function filterStroke(
  stroke: ReadonlyArray<GridCoord>,
  plan: Plan,
  importedCity: ImportedCity | null,
  roadCityentityId: string,
): { cityentityId: string; at: GridCoord }[] {
  // Index existing road tiles. A road-typed building can be 1×1 or rarely
  // larger; expand its full footprint into the set so we don't try to
  // re-place on a tile that's already covered.
  const existingRoadTiles = new Set<string>()
  for (const b of plan.buildings) {
    const r = resolveBuilding(b.cityentityId, importedCity ?? undefined)
    if (!r?.isRoad) continue
    const w = r.width > 0 ? r.width : 1
    const h = r.height > 0 ? r.height : 1
    for (let dx = 0; dx < w; dx++) {
      for (let dy = 0; dy < h; dy++) {
        existingRoadTiles.add(`${b.x + dx},${b.y + dy}`)
      }
    }
  }

  const seen = new Set<string>()
  const out: { cityentityId: string; at: GridCoord }[] = []
  for (const sq of stroke) {
    const key = `${sq.x},${sq.y}`
    if (seen.has(key)) continue
    seen.add(key)
    if (existingRoadTiles.has(key)) continue
    out.push({ cityentityId: roadCityentityId, at: sq })
  }
  return out
}
